import React, { useState } from 'react';
import { BottomNavigation, BottomNavigationAction, Paper, Menu, MenuItem } from '@mui/material';
import {
  BarChart as DashboardIcon,
  Checklist as ChecklistIcon,
  Notifications as NotificationsIcon,
  CalendarMonth as CalendarIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext.tsx'; // Import useTheme from ThemeContext
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme for breakpoints
import './BottomNavigationBar.css'; // Import the CSS for the bottom navigation

function BottomNavigationBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme(); // Destructure theme from ThemeContext
  const muiTheme = useMuiTheme(); // Get MUI theme for breakpoints
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm')); // Use muiTheme.breakpoints

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  // Determine the current active value for BottomNavigation
  const getCurrentValue = () => {
    if (location.pathname === '/') return 'dashboard';
    if (location.pathname === '/checklists') return 'checklists';
    if (location.pathname === '/notifications') return 'notifications';
    if (location.pathname === '/scheduled-tasks') return 'scheduled-tasks';
    return false; // No active icon for other pages
  };

  if (!isMobile) {
    return null; // Only render on mobile
  }

  return (
    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000, backgroundColor: theme.colors.whiteCardBackground }} elevation={3}>
      <BottomNavigation
        showLabels={false}
        value={getCurrentValue()}
        sx={{
          backgroundColor: theme.colors.whiteCardBackground,
          '& .MuiBottomNavigationAction-root': {
            color: theme.colors.darkText, // Default color for inactive icons/labels
            '&.Mui-selected': {
              color: theme.colors.primaryRed, // Active color
            },
          },
        }}
      >
        <BottomNavigationAction
          value="dashboard"
          icon={<DashboardIcon />}
          onClick={() => navigate('/')}
        />
        <BottomNavigationAction
          value="checklists"
          icon={<ChecklistIcon />}
          onClick={() => navigate('/checklists')}
        />
        <BottomNavigationAction
          value="notifications"
          icon={<NotificationsIcon />}
          onClick={() => navigate('/notifications')}
        />
        <BottomNavigationAction
          value="scheduled-tasks"
          icon={<CalendarIcon />}
          onClick={() => navigate('/scheduled-tasks')}
        />
        <BottomNavigationAction
          icon={<MenuIcon />}
          onClick={handleMenuClick}
        />
      </BottomNavigation>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.whiteCardBackground,
            borderRadius: theme.borderRadius,
            boxShadow: theme.boxShadow,
          },
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('/reports')} sx={{ color: theme.colors.darkText }}>Reports</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/settings')} sx={{ color: theme.colors.darkText }}>Settings</MenuItem>
        {/* Add other pages here if needed */}
      </Menu>
    </Paper>
  );
}

export default BottomNavigationBar;
